import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { DeliveryNoteBundlesV3Service } from 'api-v3/services';
import { CarrierDeliveryNoteBundlesArrayV3, SortType } from 'api-v3/models';
import { FoldersStore } from '@app/stores/foldersStore';
import { FolderData } from '@app/shared/models/store-types';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';
import {
    getFullTextSearchParam,
    withColumnFilters,
} from '@app/shared/utils/paginated-search';

@Injectable({
    providedIn: 'root',
})
export class CarrierFoldersPaginatedService {
    private foldersStore = inject(FoldersStore);
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);

    constructor(
        private deliveryNoteBundlesV3Service: DeliveryNoteBundlesV3Service
    ) {}

    async loadCarrierFoldersPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        // Prevent duplicate calls if already loading
        if (this.foldersStore.loading()) {
            return;
        }

        this.foldersStore.setFoldersLoading(true);
        this.foldersStore.setFoldersError(null);

        // Cancel any ongoing requests and get new abort signal
        const abortSignal = this.searchStore.cancelOngoingRequests();

        try {
            const searchText = this.searchStore.searchText();
            const pagination = this.searchStore.pagination();
            const sort = this.searchStore.sort();
            const columnFilters = this.searchStore.columnFilters();

            // Get selected locations and showClosed filter
            const selectedSiteKeys = this.appStore.getSelectedLocationKeys('carrier');
            const showClosed = this.appStore.showClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            const sortBy = sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // One query param per active column filter, AND-ed with each other
            // and with the full-text search by the backend.
            const params = withColumnFilters(
                {
                    organizationSiteKey,
                    organizationSiteKeys:
                        selectedSiteKeys.length > 0
                            ? selectedSiteKeys
                            : undefined,
                    limit: pagination.limit,
                    offset: pagination.offset,
                    sort: sortDirection,
                    sortBy: validSortBy,
                    filterNotClosed: !showClosed,
                    search: getFullTextSearchParam(searchText),
                },
                columnFilters
            );

            // This will go through the HTTP interceptors
            const response = await lastValueFrom(
                this.deliveryNoteBundlesV3Service.getCarrierDeliveryNoteBundlesV3(
                    params
                )
            );

            if (response && response.data) {
                // Map the response to include orgSiteKey and ensure proper typing
                const foldersWithOrgSiteKey: FolderData[] = response.data.map(
                    bundle => ({
                        ...bundle,
                        orgSiteKey: organizationSiteKey,
                    })
                );

                this.foldersStore.setFolders(foldersWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                this.searchStore.updatePaginationFromApiResponse(response.paginationInfo);
            }
        } catch (error: any) {
            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_FOLDERS|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_FOLDERS';

            this.foldersStore.clearFolders();
            this.foldersStore.setFoldersError(errorMessage);

            // Reset pagination to empty state on error
            this.searchStore.resetPaginationToEmpty();
        } finally {
            this.foldersStore.setFoldersLoading(false);
        }
    }
}
